import React, { useCallback, useState, useEffect } from 'react';
// Screens redesign: the new ScreensView is a drop-in replacement for the
// legacy TVList and preserves the same props surface. To revert, swap the
// import back to `../components/TVList.jsx`.
import TVList from '../components/screens/ScreensView.jsx';
import ContentSender from '../components/ContentSender.jsx';
import { DeviceAssignment } from '../components/DeviceAssignment.jsx';
import ContentDashboard from '../components/ContentDashboard.jsx';
import WebsiteContentUploader from '../components/WebsiteContentUploader.jsx';
import AnalyticsView from '../components/AnalyticsView.jsx';
import CitiesView from '../components/CitiesView.jsx';
import tvSocket from '../sockets/tvSocket';
import { fetchAllTVs } from '../api/tvApi';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { getActiveProjectId } from '../constants/projectConstants';
import { useCity } from '../contexts/CityContext';
import notificationStore from '../services/notificationStore';
import { watchScreenStatuses, registerScreenNames } from '../services/screenStatusWatcher';

/**
 * Dashboard Page
 * Main page for the CMS Dashboard
 */
const Dashboard = ({ onLogout }) => {
  const [tvs, setTvs] = useState([]);
  const [selectedTV, setSelectedTV] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [resolutionAlert, setResolutionAlert] = useState(null); // {deviceId, previous, new, mismatched}
  // Blast radius per device from the most recent tv:hardware:changed event.
  // TVDetails reads this to render HardwareChangeCard impact numbers
  // without a second REST round-trip. Keyed by deviceId.
  const [blastRadiusByDeviceId, setBlastRadiusByDeviceId] = useState({});
  // Multi-city: super-admins get the Cities management tab.
  const { isSuperAdmin } = useCity();

  // Refetch the TV list from the backend. Callable from child components
  // (e.g. TVDetails after a rename) so label changes propagate instantly.
  const refreshTvs = useCallback(async () => {
    try {
      const response = await fetchAllTVs(getActiveProjectId());
      let deviceList = [];
      if (response && typeof response === 'object') {
        if (Array.isArray(response.devices)) deviceList = response.devices;
        else if (Array.isArray(response)) deviceList = response;
      }
      setTvs(deviceList);
      // Keep selectedTV in sync — if it was updated, reflect the new fields
      setSelectedTV((current) => {
        if (!current) return null;
        const match = deviceList.find(
          (d) => (d.deviceId && d.deviceId === current.deviceId) ||
                 (d.tvId && d.tvId === current.tvId)
        );
        return match || current;
      });
      return deviceList;
    } catch (err) {
      console.warn('Failed to refresh TV list:', err);
      return null;
    }
  }, []);

  const initializeDashboard = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Step 1: Connect to Socket.IO (non-blocking)
      try {
        tvSocket.connect();
        setSocketConnected(tvSocket.isConnected());

        // Listen for connection state changes
        tvSocket.onConnect(() => {
          console.log('Socket connected - updating UI');
          setSocketConnected(true);
        });

        tvSocket.onDisconnect(() => {
          console.log('Socket disconnected - updating UI');
          setSocketConnected(false);
        });
      } catch (socketErr) {
        console.warn('Socket connection failed:', socketErr);
        setSocketConnected(false);
      }

      // Step 2: Fetch initial TV list from REST API
      try {
        const response = await fetchAllTVs(getActiveProjectId());
        // Handle new API response structure: { success, count, devices }
        let deviceList = [];
        if (response && typeof response === 'object') {
          if (Array.isArray(response.devices)) {
            deviceList = response.devices;
          } else if (Array.isArray(response)) {
            deviceList = response;
          }
        }
        setTvs(deviceList);
      } catch (apiErr) {
        console.warn('Failed to fetch TVs from API:', apiErr);
        setTvs([]);
      }

      // Step 3: Listen for real-time device status updates via socket.
      // The socket broadcast only has in-memory fields (deviceId, status,
      // lastSeen) — it lacks MongoDB fields like profileId, displayName,
      // nativeResolution. So instead of replacing tvs with the stripped-down
      // socket data, we merge: update status/lastSeen on existing records
      // and trigger a full REST refresh for new devices.
      try {
        tvSocket.onTVListUpdate((payload) => {
          // payload = { tvs, devices } (or, defensively, a bare array).
          const tvsArr = Array.isArray(payload) ? payload : (payload?.tvs || []);
          const devicesArr = Array.isArray(payload) ? [] : (payload?.devices || []);

          // Notifications: the broadcast's `devices` array carries the live
          // status for every device (the `tvs`/assigned view is usually
          // empty), so feed that — near-instant, cross-city, no extra polling.
          // Falls back to tvs if devices is absent. ContentDashboard's existing
          // 15s fetch is the secondary path when the socket is down.
          watchScreenStatuses(devicesArr.length ? devicesArr : tvsArr);

          // Live status merge into the Screens-tab list stays on the assigned
          // `tvs` view (scoped); merging the full devices array here would make
          // every cross-city device look "new" and spam refreshTvs().
          const socketDevices = tvsArr;
          if (socketDevices.length === 0) return;

          setTvs((prev) => {
            if (prev.length === 0) return prev; // wait for REST fetch first
            const prevMap = new Map(prev.map((d) => [d.deviceId || d.tvId, d]));
            let hasNewDevice = false;
            for (const sd of socketDevices) {
              const key = sd.deviceId || sd.tvId;
              const existing = prevMap.get(key);
              if (existing) {
                // Merge: keep MongoDB fields, update live status fields
                prevMap.set(key, {
                  ...existing,
                  status: sd.status ?? existing.status,
                  lastSeenAt: sd.lastSeen ?? sd.lastSeenAt ?? existing.lastSeenAt,
                  socketId: sd.socketId,
                });
              } else {
                hasNewDevice = true;
              }
            }
            // If a brand-new device appeared, do a full REST refresh to get
            // all MongoDB fields. We don't add the stripped socket record.
            if (hasNewDevice) refreshTvs();
            return Array.from(prevMap.values());
          });
        });
      } catch (socketErr) {
        console.warn('Failed to setup socket listeners:', socketErr);
      }

      // Listen for screen resolution changes. The backend persists the new
      // value to MongoDB and emits the event; we also call refreshTvs() so
      // the local tvs state pulls the new resolution and every component
      // (TVList, TVDetails, ScheduleEventModal) re-renders with the
      // updated nativeResolution. Without this refresh the alert banner
      // would show but the rest of the dashboard would stay stale until
      // a full page reload.
      tvSocket.onResolutionChanged((data) => {
        console.log('⚠️ Screen resolution changed:', data);
        setResolutionAlert({
          type: 'changed',
          deviceId: data.deviceId,
          tvId: data.tvId,
          previous: data.previousResolution,
          newRes: data.newResolution,
          timestamp: data.timestamp,
        });
        notificationStore.push({
          type: 'resolution-changed',
          severity: 'warning',
          title: 'Screen resolution changed',
          message: `${data.tvId || data.deviceId || 'A screen'} is now ${data.newResolution?.width}x${data.newResolution?.height}.`,
          deviceId: data.deviceId || data.tvId,
          projectId: data.projectId || null,
        });
        refreshTvs();
      });

      tvSocket.onResolutionMismatch((data) => {
        console.log('🚫 Resolution mismatch detected:', data);
        setResolutionAlert({
          type: 'mismatch',
          deviceId: data.deviceId,
          tvId: data.tvId,
          newRes: data.newResolution,
          mismatchedSlots: data.mismatchedSlots,
          timestamp: data.timestamp,
        });
        const slotCount = data.mismatchedSlots?.length || 0;
        notificationStore.push({
          type: 'resolution-mismatch',
          severity: 'critical',
          title: 'Resolution mismatch — content may look wrong',
          message: `${data.tvId || data.deviceId || 'A screen'} changed to ${data.newResolution?.width}x${data.newResolution?.height}${slotCount ? ` — ${slotCount} scheduled slot(s) no longer fit.` : '.'}`,
          deviceId: data.deviceId || data.tvId,
          projectId: data.projectId || null,
        });
        refreshTvs();
      });

      // Screen-profile system: hardware fingerprint changed in a way the
      // active profile didn't silence. Cache the blast radius so TVDetails
      // can show it inline without a second fetch, then refresh the device
      // list so the HardwareChangeCard appears on the Screens tab row.
      tvSocket.onHardwareChanged((data) => {
        console.log('🔧 Hardware changed:', data);
        if (data?.deviceId) {
          setBlastRadiusByDeviceId((prev) => ({
            ...prev,
            [data.deviceId]: data.blastRadius || null,
          }));
        }
        const broken = data?.blastRadius?.broken || 0;
        notificationStore.push({
          type: 'hardware-changed',
          severity: broken > 0 ? 'critical' : 'warning',
          title: 'Screen hardware changed',
          message: `${data.tvId || data.deviceId || 'A screen'} reported a hardware change${broken > 0 ? ` — ${broken} scheduled slot(s) affected.` : '.'}`,
          deviceId: data.deviceId || data.tvId,
          projectId: data.projectId || null,
        });
        refreshTvs();
      });

      // The tv:health:update subscription moved to the internal Watchdog
      // Console. The backend still broadcasts it namespace-wide and the health
      // subdoc still rides every device response — this client-facing app just
      // no longer renders playback diagnostics, so it has nothing to merge.

      setIsLoading(false);
    } catch (err) {
      console.error('Error initializing dashboard:', err);
      setError('Failed to load dashboard. Please check your backend connection.');
      setIsLoading(false);
    }
  };

  // Feed assigned screen names (from the REST-sourced tvs list, which carries
  // displayName) into the watcher so offline/online alerts show the name the
  // user assigned rather than the raw deviceId. Runs on every tab.
  useEffect(() => {
    registerScreenNames(tvs);
  }, [tvs]);

  // Initialize dashboard on mount
  useEffect(() => {
    const initTimeout = setTimeout(() => {
      initializeDashboard();
    }, 0);

    return () => {
      clearTimeout(initTimeout);
      tvSocket.offTVListUpdate();
      tvSocket.offResolutionEvents();
      tvSocket.disconnect();
    };
  }, []);

  const handleSelectTV = (tv) => {
    setSelectedTV(tv);
  };

  const getTitleForTab = (tab) => {
    switch (tab) {
      case 'history': return 'Calendar';
      case 'tvList': return 'Screens';
      case 'websiteUpload': return 'Media';
      case 'analytics': return 'Analytics';
      case 'cities': return 'Cities';
      case 'screensaver': return 'Screensaver';
      default: return 'Calendar';
    }
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-color)',
        color: 'var(--text-secondary)'
      }}>
        <div>⏳ Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: 'var(--bg-color)'
    }}>
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isSuperAdmin={isSuperAdmin}
      />

      {/* Main Content Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Header
          title={getTitleForTab(activeTab)}
          connectionStatus={socketConnected}
          activeTab={activeTab}
          onMenuClick={() => setSidebarOpen(true)}
        />

        <main className="app-main" style={{ flex: 1, padding: '32px', minWidth: 0, overflow: 'hidden' }}>
          {/* Resolution change alert */}
          {resolutionAlert && (
            <div style={{
              backgroundColor: resolutionAlert.type === 'mismatch' ? '#fef2f2' : '#fffbeb',
              border: `1px solid ${resolutionAlert.type === 'mismatch' ? '#fecaca' : '#fde68a'}`,
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
            }}>
              <span style={{ fontSize: 20 }}>{resolutionAlert.type === 'mismatch' ? '🚫' : '⚠️'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: resolutionAlert.type === 'mismatch' ? '#dc2626' : '#92400e' }}>
                  {resolutionAlert.type === 'mismatch'
                    ? 'Screen Resolution Mismatch — Content May Look Wrong'
                    : 'Screen Resolution Changed'}
                </div>
                <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                  Device <strong>{resolutionAlert.deviceId}</strong>
                  {resolutionAlert.previous && (
                    <> changed from {resolutionAlert.previous.width}x{resolutionAlert.previous.height}</>
                  )}
                  {' '}to <strong>{resolutionAlert.newRes.width}x{resolutionAlert.newRes.height}</strong>
                  {resolutionAlert.newRes.aspectRatio && ` (${resolutionAlert.newRes.aspectRatio})`}
                </div>
                {resolutionAlert.mismatchedSlots && resolutionAlert.mismatchedSlots.length > 0 && (
                  <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>
                    {resolutionAlert.mismatchedSlots.length} active schedule(s) have wrong aspect ratio:
                    {resolutionAlert.mismatchedSlots.map((s, i) => (
                      <span key={i} style={{ marginLeft: 4, background: '#fee2e2', padding: '1px 6px', borderRadius: 3 }}>
                        {s.contentId} ({s.contentResolution})
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setResolutionAlert(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af', padding: '0 4px' }}
              >×</button>
            </div>
          )}
          {/* Error banner */}
          {error && (
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '8px',
              padding: '16px 24px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                  Backend Connection Issue
                </div>
                <div style={{ fontSize: '14px', color: '#78350f' }}>
                  {error} The dashboard will work once the backend is available.
                </div>
              </div>
              <button
                onClick={initializeDashboard}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                🔄 Retry
              </button>
            </div>
          )}

          {/* Content */}
          {activeTab === 'tvList' ? (
            <div style={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
              <TVList
                tvs={tvs}
                selectedTV={selectedTV}
                onSelectTV={handleSelectTV}
                onRefreshTvs={refreshTvs}
                blastRadiusByDeviceId={blastRadiusByDeviceId}
              />
            </div>
          ) : activeTab === 'websiteUpload' ? (
            <WebsiteContentUploader tvs={tvs} selectedTV={selectedTV} />
          ) : activeTab === 'screensaver' ? (
            <WebsiteContentUploader tvs={tvs} selectedTV={selectedTV} screensaverOnly />
          ) : activeTab === 'analytics' ? (
            <AnalyticsView tvs={tvs} />
          ) : activeTab === 'cities' ? (
            <CitiesView />
          ) : (
            <ContentDashboard />
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
